import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Sparkles, Image, Star, Loader2, Search, Layers, Palette, RotateCw, Zap, DollarSign, Repeat, TrendingUp, Target, BookOpen, FileText, PenTool, GitBranch, CheckCircle, Plus } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { assetsApi, Asset } from '@/services/api'

const API_BASE = import.meta.env.VITE_API_URL ||
  (window.location.hostname === 'localhost' ? '' : 'https://aistudio-api.wizice100.workers.dev')

export default function HomePage() {
  const { isAuthenticated } = useAuthStore()
  const [popularAssets, setPopularAssets] = useState<Asset[]>([])
  const [assetsLoading, setAssetsLoading] = useState(true)

  useEffect(() => {
    const fetchPopularAssets = async () => {
      setAssetsLoading(true)
      try {
        const result = await assetsApi.list({
          public: true,
          limit: 8,
          sort: 'download_count',
          order: 'DESC',
        })
        if (result.success && result.data) {
          setPopularAssets(result.data)
        }
      } catch (err) {
        console.error('Failed to fetch popular assets:', err)
      } finally {
        setAssetsLoading(false)
      }
    }
    fetchPopularAssets()
  }, [])

  const getImageUrl = (asset: Asset) => {
    if (asset.thumbnailUrl) {
      return `${API_BASE}${asset.thumbnailUrl}`
    }
    return null
  }

  return (
    <div className="min-h-[calc(100vh-56px)]">
      {/* Hero Section */}
      <section className="relative py-20 px-4 bg-gradient-dark overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent-purple/20 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-gold/20 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            콘텐츠 제작,{' '}
            <span className="text-gradient-gold">AI로 더 빠르게</span>
          </h1>
          <p className="text-lg md:text-xl text-text-secondary mb-10 max-w-2xl mx-auto">
            AI이미지 생성부터 콘텐츠 리라이팅까지, AI가 콘텐츠 제작의 모든 과정을 도와드립니다.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/editor" className="btn btn-primary text-lg px-8 py-3">
              <Plus className="w-5 h-5 mr-2" />
              AI이미지 만들기
            </Link>
            <Link to="/rewrite/create" className="btn btn-secondary text-lg px-8 py-3">
              <PenTool className="w-5 h-5 mr-2" />
              콘텐츠 리라이팅
            </Link>
            <Link to="/assets" className="btn btn-ghost border border-border-subtle text-lg px-8 py-3">
              <Image className="w-5 h-5 mr-2" />
              Asset 둘러보기
            </Link>
          </div>
        </div>
      </section>


      {/* Two Product Cards */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">AI Studio 핵심 서비스</h2>
          <p className="text-center text-text-secondary text-lg mb-12 max-w-2xl mx-auto">
            AI이미지 생성과 콘텐츠 리라이팅, 두 가지 핵심 기능으로 콘텐츠 제작을 지원합니다
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Cover Design */}
            <div className="card-elevated p-8 border-t-4 border-accent-gold hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
                  <Palette className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">AI이미지 생성</h3>
                  <p className="text-sm text-text-secondary">전문가 수준의 AI이미지를 직접 제작</p>
                </div>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3 text-text-secondary">
                  <Search className="w-5 h-5 text-accent-gold flex-shrink-0 mt-0.5" />
                  <span>레퍼런스 이미지 업로드 및 AI 자동 분석</span>
                </li>
                <li className="flex items-start gap-3 text-text-secondary">
                  <Layers className="w-5 h-5 text-accent-gold flex-shrink-0 mt-0.5" />
                  <span>배경, 인물, 타이포 등 5,000+ 소재 라이브러리</span>
                </li>
                <li className="flex items-start gap-3 text-text-secondary">
                  <Sparkles className="w-5 h-5 text-accent-gold flex-shrink-0 mt-0.5" />
                  <span>Gemini, DALL-E 등 다양한 AI 모델로 이미지 생성</span>
                </li>
                <li className="flex items-start gap-3 text-text-secondary">
                  <RotateCw className="w-5 h-5 text-accent-gold flex-shrink-0 mt-0.5" />
                  <span>요소별 재생성 및 비주얼 편집기</span>
                </li>
              </ul>
              <Link to="/editor" className="btn btn-primary w-full justify-center">
                AI이미지 만들기 <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>

            {/* Rewrite */}
            <div className="card-elevated p-8 border-t-4 border-accent-purple hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
                  <PenTool className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">AI 콘텐츠 리라이팅</h3>
                  <p className="text-sm text-text-secondary">원본을 다양한 채널에 맞게 변환</p>
                </div>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3 text-text-secondary">
                  <FileText className="w-5 h-5 text-accent-purple flex-shrink-0 mt-0.5" />
                  <span>원본 콘텐츠 입력 후 카드뉴스/블로그 등 멀티채널 동시 생성</span>
                </li>
                <li className="flex items-start gap-3 text-text-secondary">
                  <GitBranch className="w-5 h-5 text-accent-purple flex-shrink-0 mt-0.5" />
                  <span>채널별 톤앤매너, 섹션 구조, 글자수 가이드 적용</span>
                </li>
                <li className="flex items-start gap-3 text-text-secondary">
                  <RefreshIcon className="w-5 h-5 text-accent-purple flex-shrink-0 mt-0.5" />
                  <span>섹션별 AI 리라이트, 버전 히스토리 및 복원</span>
                </li>
                <li className="flex items-start gap-3 text-text-secondary">
                  <CheckCircle className="w-5 h-5 text-accent-purple flex-shrink-0 mt-0.5" />
                  <span>초안 → 검토 → 확정 워크플로우 및 내보내기</span>
                </li>
              </ul>
              <Link to="/rewrite/create" className="btn btn-secondary w-full justify-center">
                리라이팅 시작 <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Assets Section */}
      <section className="py-12 px-4 bg-bg-secondary">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">인기 Asset</h2>
            <Link to="/assets" className="flex items-center text-text-secondary hover:text-accent-gold transition-colors">
              전체보기 <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>

          {assetsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-accent-gold" />
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {popularAssets.map((asset) => (
                <Link key={asset.id} to="/assets" className="card-elevated group cursor-pointer">
                  <div className="aspect-[3/4] bg-bg-elevated relative overflow-hidden">
                    {getImageUrl(asset) ? (
                      <img
                        src={getImageUrl(asset)!}
                        alt={asset.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-text-tertiary">
                        <Image className="w-8 h-8" />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className="font-medium truncate text-sm">{asset.name}</h3>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-text-tertiary capitalize">{asset.type}</span>
                      <span className="flex items-center text-accent-gold">
                        <Star className="w-3 h-3 mr-1 fill-current" />
                        {asset.viewCount || 0}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Features Section - Cover */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">AI이미지 생성 기능</h2>
          <p className="text-center text-text-secondary text-lg mb-12 max-w-2xl mx-auto">
            AI이미지 제작의 모든 과정을 AI가 도와드립니다
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="card-elevated p-6 border-l-4 border-accent-gold hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
                  <Search className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold">AI 이미지 분석기</h3>
              </div>
              <p className="text-text-secondary mb-4">
                마음에 드는 이미지를 업로드하면 AI가 배경, 인물, 타이포 등을 자동 분석합니다.
              </p>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li className="flex items-start gap-2">
                  <span className="text-accent-gold mt-0.5">&#10003;</span>
                  <span>배경, 인물, 텍스트 자동 추출</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent-gold mt-0.5">&#10003;</span>
                  <span>분위기와 스타일 상세 설명</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent-gold mt-0.5">&#10003;</span>
                  <span>Gemini, Claude 등 다양한 AI 모델 지원</span>
                </li>
              </ul>
            </div>

            <div className="card-elevated p-6 border-l-4 border-accent-purple hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center">
                  <Layers className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold">소재 관리 시스템</h3>
              </div>
              <p className="text-text-secondary mb-4">
                배경, 인물, 타이포 등 모든 소재를 체계적으로 관리하고 검색할 수 있습니다.
              </p>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li className="flex items-start gap-2">
                  <span className="text-accent-gold mt-0.5">&#10003;</span>
                  <span>5가지 카테고리별 분류</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent-gold mt-0.5">&#10003;</span>
                  <span>AI 의미 검색 및 유사 이미지 검색</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent-gold mt-0.5">&#10003;</span>
                  <span>태그 자동 분류 및 즐겨찾기</span>
                </li>
              </ul>
            </div>

            <div className="card-elevated p-6 border-l-4 border-accent-gold hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold">AI 이미지 생성기</h3>
              </div>
              <p className="text-text-secondary mb-4">
                텍스트 설명만으로 새 이미지를 생성하거나, 소재들을 자연스럽게 합성합니다.
              </p>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li className="flex items-start gap-2">
                  <span className="text-accent-gold mt-0.5">&#10003;</span>
                  <span>프롬프트 모드: 완전히 새로운 이미지 생성</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent-gold mt-0.5">&#10003;</span>
                  <span>합성 모드: 여러 소재를 하나로 합성</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent-gold mt-0.5">&#10003;</span>
                  <span>Gemini, DALL-E, Stable Diffusion 등</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Rewrite */}
      <section className="py-16 px-4 bg-bg-secondary">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">콘텐츠 리라이팅 기능</h2>
          <p className="text-center text-text-secondary text-lg mb-12 max-w-2xl mx-auto">
            하나의 원본 콘텐츠를 다양한 채널에 맞게 자동 변환합니다
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="card-elevated p-6 border-l-4 border-accent-purple hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold">멀티채널 생성</h3>
              </div>
              <p className="text-text-secondary mb-4">
                원본 텍스트를 입력하면 카드뉴스, 블로그 등 여러 채널에 맞는 콘텐츠를 동시에 생성합니다.
              </p>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li className="flex items-start gap-2">
                  <span className="text-accent-purple mt-0.5">&#10003;</span>
                  <span>카드뉴스, 블로그 등 다양한 채널 지원</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent-purple mt-0.5">&#10003;</span>
                  <span>채널별 톤앤매너 자동 적용</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent-purple mt-0.5">&#10003;</span>
                  <span>섹션별 글자수 제한 가이드</span>
                </li>
              </ul>
            </div>

            <div className="card-elevated p-6 border-l-4 border-accent-purple hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-500 to-violet-700 flex items-center justify-center">
                  <GitBranch className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold">버전 관리</h3>
              </div>
              <p className="text-text-secondary mb-4">
                편집할 때마다 자동으로 버전이 저장되어, 언제든 이전 버전으로 복원할 수 있습니다.
              </p>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li className="flex items-start gap-2">
                  <span className="text-accent-purple mt-0.5">&#10003;</span>
                  <span>자동 버전 히스토리 기록</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent-purple mt-0.5">&#10003;</span>
                  <span>원클릭 버전 복원</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent-purple mt-0.5">&#10003;</span>
                  <span>변경 내역 요약 표시</span>
                </li>
              </ul>
            </div>

            <div className="card-elevated p-6 border-l-4 border-accent-purple hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-fuchsia-500 to-fuchsia-700 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold">워크플로우 관리</h3>
              </div>
              <p className="text-text-secondary mb-4">
                초안에서 검토, 확정까지 상태를 관리하고 완성된 콘텐츠를 내보낼 수 있습니다.
              </p>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li className="flex items-start gap-2">
                  <span className="text-accent-purple mt-0.5">&#10003;</span>
                  <span>초안 → 검토 → 확정 상태 전환</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent-purple mt-0.5">&#10003;</span>
                  <span>클립보드 복사 및 텍스트 파일 내보내기</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent-purple mt-0.5">&#10003;</span>
                  <span>채널별 가이드라인 커스텀 설정</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">주요 장점</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="card-elevated p-6 text-center hover:bg-gradient-to-br hover:from-purple-500 hover:to-purple-700 hover:text-white transition-all group">
              <Zap className="w-8 h-8 mx-auto mb-3 text-accent-gold group-hover:text-white" />
              <h4 className="font-semibold mb-2">빠른 제작</h4>
              <p className="text-sm text-text-secondary group-hover:text-white/90">몇 분 안에 전문가 수준 완성</p>
            </div>
            <div className="card-elevated p-6 text-center hover:bg-gradient-to-br hover:from-purple-500 hover:to-purple-700 hover:text-white transition-all group">
              <DollarSign className="w-8 h-8 mx-auto mb-3 text-accent-gold group-hover:text-white" />
              <h4 className="font-semibold mb-2">비용 절감</h4>
              <p className="text-sm text-text-secondary group-hover:text-white/90">외주 없이 직접 제작</p>
            </div>
            <div className="card-elevated p-6 text-center hover:bg-gradient-to-br hover:from-purple-500 hover:to-purple-700 hover:text-white transition-all group">
              <Repeat className="w-8 h-8 mx-auto mb-3 text-accent-gold group-hover:text-white" />
              <h4 className="font-semibold mb-2">무한한 시도</h4>
              <p className="text-sm text-text-secondary group-hover:text-white/90">마음에 들 때까지 재생성</p>
            </div>
            <div className="card-elevated p-6 text-center hover:bg-gradient-to-br hover:from-purple-500 hover:to-purple-700 hover:text-white transition-all group">
              <BookOpen className="w-8 h-8 mx-auto mb-3 text-accent-gold group-hover:text-white" />
              <h4 className="font-semibold mb-2">멀티채널</h4>
              <p className="text-sm text-text-secondary group-hover:text-white/90">하나의 소스로 여러 채널 콘텐츠</p>
            </div>
            <div className="card-elevated p-6 text-center hover:bg-gradient-to-br hover:from-purple-500 hover:to-purple-700 hover:text-white transition-all group">
              <TrendingUp className="w-8 h-8 mx-auto mb-3 text-accent-gold group-hover:text-white" />
              <h4 className="font-semibold mb-2">트렌드 분석</h4>
              <p className="text-sm text-text-secondary group-hover:text-white/90">인기 이미지 분석으로 파악</p>
            </div>
            <div className="card-elevated p-6 text-center hover:bg-gradient-to-br hover:from-purple-500 hover:to-purple-700 hover:text-white transition-all group">
              <Target className="w-8 h-8 mx-auto mb-3 text-accent-gold group-hover:text-white" />
              <h4 className="font-semibold mb-2">일관성 유지</h4>
              <p className="text-sm text-text-secondary group-hover:text-white/90">가이드라인 기반 품질 관리</p>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow Diagram */}
      <section className="py-16 px-4 bg-bg-secondary">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">AI Studio 워크플로우</h2>
          <p className="text-center text-text-secondary text-lg mb-8">
            AI이미지 생성과 콘텐츠 리라이팅의 모든 과정을 한눈에 확인하세요
          </p>
          <div className="card-elevated p-4 md:p-8 bg-white">
            <img
              src="/workflow-diagram.png"
              alt="AI Studio 워크플로우 다이어그램"
              className="w-full h-auto rounded-lg shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">상세 사용 흐름</h2>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Cover workflow */}
            <div>
              <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                <Palette className="w-5 h-5 text-accent-gold" /> AI이미지 생성
              </h3>
              <div className="relative">
                <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-amber-500 to-amber-700" />
                <div className="space-y-6">
                  {[
                    { step: 1, title: '레퍼런스 분석', desc: '마음에 드는 이미지를 분석기에 업로드' },
                    { step: 2, title: '소재 검색', desc: '소재 라이브러리에서 배경, 인물 검색' },
                    { step: 3, title: 'AI 생성', desc: '소재 합성 또는 새로운 이미지 생성' },
                    { step: 4, title: '편집 및 완성', desc: '편집기에서 다듬고 다운로드' },
                  ].map((item) => (
                    <div key={item.step} className="flex gap-4 items-start relative">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 text-white flex items-center justify-center font-bold z-10 shadow-lg text-sm">
                        {item.step}
                      </div>
                      <div className="flex-1 pt-2">
                        <h4 className="font-bold text-accent-gold">{item.title}</h4>
                        <p className="text-sm text-text-secondary">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Rewrite workflow */}
            <div>
              <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                <PenTool className="w-5 h-5 text-accent-purple" /> 콘텐츠 리라이팅
              </h3>
              <div className="relative">
                <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-purple-500 to-purple-700" />
                <div className="space-y-6">
                  {[
                    { step: 1, title: '소스 입력', desc: '원본 콘텐츠를 붙여넣기' },
                    { step: 2, title: '채널 선택', desc: '카드뉴스, 블로그 등 대상 채널 선택' },
                    { step: 3, title: 'AI 생성', desc: '채널별 톤앤매너에 맞게 자동 생성' },
                    { step: 4, title: '편집 및 확정', desc: '섹션별 수정, 버전 관리, 내보내기' },
                  ].map((item) => (
                    <div key={item.step} className="flex gap-4 items-start relative">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 text-white flex items-center justify-center font-bold z-10 shadow-lg text-sm">
                        {item.step}
                      </div>
                      <div className="flex-1 pt-2">
                        <h4 className="font-bold text-accent-purple">{item.title}</h4>
                        <p className="text-sm text-text-secondary">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {!isAuthenticated && (
        <section className="py-16 px-4 bg-gradient-dark">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">지금 시작하세요</h2>
            <p className="text-text-secondary mb-8">
              무료로 가입하고 AI Studio의 모든 기능을 체험해보세요.
            </p>
            <Link to="/login" className="btn btn-primary text-lg px-8 py-3">
              무료로 시작하기
            </Link>
          </div>
        </section>
      )}
    </div>
  )
}

function RefreshIcon({ className }: { className?: string }) {
  return <RotateCw className={className} />
}
